import { useCallback, useRef } from 'react';
import { GripVertical, Plus, X } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { inputBase } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// =====================================================================
// StringListEditor — inline list editor for string[] arrays.
//
// Used by pricing.features (and later for footer.links labels, contact
// form-field labels, etc.). Each row is a single input — no
// collapsed/expanded model since there's nothing to expand into.
// Drag handle on the left, input fills the row, ✕ delete on the right
// (visible on row hover/focus, like the Repeater).
// =====================================================================

function makeId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

interface StringListEditorProps {
  values: string[];
  onChange: (next: string[]) => void;
  addLabel: string;
  placeholder?: string;
  /** Default value for a freshly added row. */
  newItem?: string;
}

export function StringListEditor({
  values,
  onChange,
  addLabel,
  placeholder,
  newItem = '',
}: StringListEditorProps) {
  const idsRef = useRef<string[]>([]);
  while (idsRef.current.length < values.length) idsRef.current.push(makeId());
  if (idsRef.current.length > values.length) {
    idsRef.current = idsRef.current.slice(0, values.length);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const updateAt = useCallback(
    (index: number, next: string) => {
      onChange(values.map((v, i) => (i === index ? next : v)));
    },
    [values, onChange],
  );

  const deleteAt = useCallback(
    (index: number) => {
      idsRef.current = idsRef.current.filter((_, i) => i !== index);
      onChange(values.filter((_, i) => i !== index));
    },
    [values, onChange],
  );

  const add = useCallback(() => {
    idsRef.current = [...idsRef.current, makeId()];
    onChange([...values, newItem]);
  }, [values, onChange, newItem]);

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const from = idsRef.current.indexOf(String(active.id));
      const to = idsRef.current.indexOf(String(over.id));
      if (from < 0 || to < 0) return;
      idsRef.current = arrayMove(idsRef.current, from, to);
      onChange(arrayMove(values, from, to));
    },
    [values, onChange],
  );

  const ids = idsRef.current;

  return (
    <div className="flex flex-col gap-1.5">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {values.map((value, index) => {
            const id = ids[index];
            if (!id) return null;
            return (
              <StringRow
                key={id}
                rowId={id}
                value={value}
                {...(placeholder !== undefined ? { placeholder } : {})}
                onChange={(next) => updateAt(index, next)}
                onDelete={() => deleteAt(index)}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      <button
        type="button"
        onClick={add}
        className={cn(
          'inline-flex items-center gap-1.5 self-start',
          'h-8 px-3 mt-0.5',
          'text-xs font-medium text-brand-700',
          'bg-brand-50 hover:bg-brand-100',
          'border border-dashed border-brand-200',
          'rounded-[var(--radius-sm)]',
          'transition-colors duration-[var(--duration-fast)]',
          'focus-visible:outline-2 focus-visible:outline-brand-500',
        )}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        {addLabel}
      </button>
    </div>
  );
}

interface StringRowProps {
  rowId: string;
  value: string;
  placeholder?: string;
  onChange: (next: string) => void;
  onDelete: () => void;
}

function StringRow({ rowId, value, placeholder, onChange, onDelete }: StringRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rowId,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group/srow',
        'flex items-center gap-1',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        className={cn(
          'shrink-0 p-1.5 rounded',
          'text-text-tertiary hover:text-text-secondary',
          'cursor-grab active:cursor-grabbing',
          'focus-visible:outline-2 focus-visible:outline-brand-500',
        )}
      >
        <GripVertical className="h-3.5 w-3.5" aria-hidden />
      </button>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        // ONE input recipe (design audit T12/B2 → T14): inputBase carries
        // the border, surface, radius, placeholder and focus ring; only the
        // inspector's compact height and its flex sizing stay local.
        className={cn(inputBase, 'flex-1 min-w-0 h-8 py-1')}
      />
      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete"
        className={cn(
          'shrink-0 p-1.5 rounded text-text-tertiary',
          'opacity-0 group-hover/srow:opacity-100 focus-visible:opacity-100',
          'hover:text-danger-fg hover:bg-danger-bg',
          'focus-visible:outline-2 focus-visible:outline-brand-500',
          'transition-opacity duration-[var(--duration-fast)]',
        )}
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
